import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { DimensionChart } from '../../../chrome/chart';
import { Absent } from '../../../chrome/measure';
import { count, share } from '../../../chrome/measure/format';
import { BALANCE_DOMAIN, type Dimension, type Reading } from './compute';
import { pub, pubPlural } from './messages';

/** Below this share of commenters a card's profiled people are too few to
 *  stand for its audience: they are the ones a capped profile pull reached. */
const THIN_SHARE = 0.2;

/** Occupation has too many members to stand as columns. */
const BARS: readonly Dimension[] = ['occupation'];

const [AXIS_MIN, AXIS_MAX] = BALANCE_DOMAIN;

/** One dimension's balance. The suppressed share leads and is large: it is the
 *  first thing a reader needs to weigh what follows. Where this screen
 *  withheld cells of its own it reads as a floor, because the share the gate
 *  returned is a share of a population this screen cannot recount. */
export function DimensionBody(props: {
  dimension: Dimension;
  title: string;
  reading: Reading;
  /** Everyone who commented, the population this card's profiles are read from. */
  commenters: number;
  /** The people the age card counts. An unknown or under-18 age keeps the age
   *  band alone, so they can outnumber every other card's. */
  ageBandTotal: number | null;
}): ReactNode {
  const { reading, title } = props;
  const locale = getLocale();
  const total = reading.total;
  // Each dimension is read from its own profiles (an unknown or under-18 age
  // keeps the age band alone), so each card weighs its own count.
  const thin = total !== null && total > 0 && total < THIN_SHARE * props.commenters;

  if (
    reading.kind === 'not-collected' &&
    props.dimension !== 'age_band' &&
    props.ageBandTotal !== null &&
    props.ageBandTotal > 0
  ) {
    return (
      <>
        <h3 className="text-title-sm">{title}</h3>
        <p className="text-body-sm text-muted-foreground">{pub('dimensions.ageOnly')}</p>
      </>
    );
  }

  if (reading.kind === 'not-collected') {
    return (
      <>
        <h3 className="text-title-sm">{title}</h3>
        <TotalLine total={reading.total} />
        <p className="text-body-sm text-muted-foreground">{pub('dimensions.notCollected')}</p>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-2">
        {reading.suppressedShare > 0 && (
          <>
            <span data-numeric className="text-title-lg">
              {share(reading.suppressedShare, locale)}
            </span>
            <span className="text-label text-muted-foreground">
              {pub(reading.narrowed ? 'withheld.floor' : 'withheld.figure')}
            </span>
          </>
        )}
        <TotalLine total={reading.total} />
      </div>
      {thin && (
        <p data-slot="thin" className="text-caption text-muted-foreground">
          {pub('dimensions.thin', { n: count(props.commenters, locale) })}
        </p>
      )}
      <div className={`flex flex-col gap-3 ${thin ? 'opacity-60' : ''}`}>
        {reading.groups.length === 0 ? (
          <>
            <h3 className="text-title-sm">{title}</h3>
            <p className="text-body-sm text-muted-foreground">{pub('dimensions.allWithheld')}</p>
          </>
        ) : (
          <DimensionChart
            title={title}
            domain={BALANCE_DOMAIN}
            orientation={BARS.includes(props.dimension) ? 'bars' : 'columns'}
            axisLabel={pub('chart.axis', {
              min: `${AXIS_MIN}`.replace('-', '−'),
              max: `${AXIS_MAX}`,
            })}
            describe={(row) =>
              [
                pub('chart.tip', {
                  score: `${row.value > 0 ? '+' : ''}${row.value}`.replace('-', '−'),
                }),
                row.n === null
                  ? null
                  : pubPlural('readings.other', row.n, { count: count(row.n, locale) }),
              ]
                .filter((part) => part !== null)
                .join(' · ')
            }
            rows={reading.groups.map((group) => ({
              label: group.value,
              value: group.balance,
              n: group.n,
            }))}
          />
        )}
      </div>
    </>
  );
}

/** The k threshold is the gate's reason for withholding a cell; it is not this
 *  screen's reason for withholding the population, which is that stating it
 *  beside what survived gives back what did not. */
function TotalLine(props: { total: number | null }): ReactNode {
  return props.total === null ? (
    <Absent reason={{ text: pub('withheld.protects') }} />
  ) : (
    <span data-numeric className="text-caption text-muted-foreground">
      {pubPlural('based.other', props.total, {
        n: count(props.total, getLocale()),
      })}
    </span>
  );
}
